import React, { useState } from 'react';
import { Search, Check, X, Building2, Loader2 } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useQuery } from '@tanstack/react-query';

interface DropInModuleProps {
  sessionId: string;
  engagementId: string;
  attendance: any[];
  onDropIn: (person: any) => void;
  onMark: (personId: string, status: string) => void;
  onDelete?: (personId: string) => void;
  isPending: boolean;
}

export const DropInModule: React.FC<DropInModuleProps> = ({ sessionId, engagementId, attendance, onDropIn, onMark, onDelete, isPending }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data: searchResults, isLoading: searching } = useQuery({
    queryKey: ['globalPeopleSearch', searchTerm],
    queryFn: async () => {
      if (searchTerm.length < 2) return [];
      const { data } = await supabase
        .from('people')
        .select('*, organizations(name)')
        .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
        .limit(6);
      return data || [];
    },
    enabled: searchTerm.length >= 2
  });

  return (
    <div className="space-y-6">
      <div className="bg-slate-900 p-8 rounded-md shadow-lg border border-slate-800">
        <h3 className="text-white text-xs font-black uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
          <Search size={14} className="text-[#FFDB00]" /> Check-In Search
        </h3>
        <div className="relative">
          <input 
            type="text"
            placeholder="Search by name or email to register..."
            className="w-full bg-white/5 border border-white/10 rounded-md px-5 py-4 text-white font-bold outline-none focus:ring-4 focus:ring-[#FFDB00]/20 focus:border-[#FFDB00] transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searching && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[#FFDB00] text-[9px] font-black uppercase animate-pulse">Searching...</div>}
        </div>

        {searchTerm.length >= 2 && searchResults && (
          <div className="mt-4 bg-white rounded-md shadow-2xl overflow-hidden divide-y divide-slate-100 animate-in slide-in-from-top-2">
            {searchResults.length === 0 ? (
              <div className="p-10 text-center text-slate-400 text-[10px] font-black uppercase italic">No identity found in registry</div>
            ) : (
              searchResults.map(person => {
                const isAlreadyPresent = attendance.some(a => a.person_id === person.id);
                return (
                  <div key={person.id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-slate-900 text-[#FFDB00] rounded-md flex items-center justify-center font-black text-xs uppercase shadow-sm">
                        {person.first_name[0]}{person.last_name[0]}
                      </div>
                      <div>
                        <p className="text-sm font-black text-slate-900 uppercase tracking-tight">{person.first_name} {person.last_name}</p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                          <Building2 size={10} /> {person.organizations?.name || 'Independent'}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={() => { onDropIn(person); setSearchTerm(''); }}
                      disabled={isAlreadyPresent || isPending}
                      className={`px-4 py-2 rounded font-black uppercase text-[9px] tracking-widest transition-all ${isAlreadyPresent ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 cursor-default' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'}`}
                    >
                      {isAlreadyPresent ? 'Already Logged' : 'Register & Check-In'}
                    </button>
                  </div>
                )
              })
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 bg-slate-50/50">
          <h2 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
            <Check size={16} className="text-blue-600" /> Currently Logged ({attendance.length})
          </h2>
        </div>
        <table className="w-full text-left">
          <thead>
            <tr className="bg-white border-b border-slate-50 text-slate-400 text-[8px] font-black uppercase tracking-[0.2em]">
              <th className="px-8 py-4">Identity</th>
              <th className="px-8 py-4 text-center">Status</th>
              <th className="px-8 py-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {attendance.length === 0 ? (
              <tr><td colSpan={3} className="p-20 text-center text-[10px] font-black text-slate-200 uppercase tracking-widest italic">No personnel checked in yet</td></tr>
            ) : (
              attendance.map(a => (
                <tr key={a.id} className="hover:bg-slate-50/50 group">
                  <td className="px-8 py-4">
                    <p className="text-xs font-black text-slate-900 uppercase tracking-tight">{a.people?.first_name} {a.people?.last_name}</p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">{a.people?.email}</p>
                  </td>
                  <td className="px-8 py-4 text-center">
                    <span className="bg-emerald-50 text-emerald-600 px-3 py-1 rounded-md text-[9px] font-black uppercase border border-emerald-100">PRESENT</span>
                  </td>
                  <td className="px-8 py-4 text-right">
                    <button 
                      onClick={() => {
                        if (a.person_id && onDelete) {
                          onDelete(a.person_id);
                        } else if (a.person_id) {
                          onMark(a.person_id, 'absent');
                        }
                      }}
                      className="p-2 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded transition-all disabled:opacity-30"
                      disabled={isPending}
                      title="Remove check-in record"
                    >
                      {isPending ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};